import React from "react";
import { ArrowUpRight } from "lucide-react";
import { Button } from "../ui/button";
import { Reveal, Overline, Starburst } from "../common";
import { useLead } from "../LeadDialog";
import { IMAGES } from "../../data/site";

const Block = ({ id, reverse, overline, title, body, points, image, alt, testid }) => {
  const { openLead } = useLead();
  return (
    <div
      id={id}
      className="max-w-[88rem] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center scroll-mt-20"
      data-testid={testid}
    >
      <Reveal className={reverse ? "lg:order-2" : ""}>
        <div className="relative">
          <div className="overflow-hidden rounded-3xl shadow-xl aspect-[4/3]">
            <img src={image} alt={alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
          </div>
          <Starburst
            className={`absolute h-14 w-14 text-jet-amber ${
              reverse ? "-left-5 -bottom-5" : "-right-5 -top-5"
            }`}
          />
        </div>
      </Reveal>

      <Reveal delay={0.1} className={reverse ? "lg:order-1" : ""}>
        <Overline className="mb-5">{overline}</Overline>
        <h2 className="font-display font-semibold text-4xl md:text-5xl leading-[0.98] tracking-tight text-jet-ink">
          {title}
        </h2>
        <p className="mt-5 text-lg text-jet-ink/70 leading-relaxed">{body}</p>
        <Button
          onClick={() => openLead("quote")}
          variant="outline"
          className="mt-8 rounded-full border-2 border-jet-ink bg-transparent hover:bg-jet-ink hover:text-white text-jet-ink font-bold h-13 px-7 py-3 group"
          data-testid={`${testid}-cta`}
        >
          Request a Quote
          <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Button>
      </Reveal>
    </div>
  );
};

export const Commercial = () => (
  <section className="py-24 md:py-28 bg-background" data-testid="commercial-section">
    <Block
      id="commercial"
      testid="commercial-block"
      overline="Commercial Cleaning"
      title="Professional cleaning for productive workspaces."
      body="A clean workplace creates a better experience for employees, customers, and visitors. We provide dependable commercial cleaning tailored to your schedule — day or evening."
      points={[
        "Offices & Suites",
        "Medical Offices",
        "Retail",
        "Break Rooms",
        "Restrooms",
        "Floor Care",
      ]}
      image={IMAGES.commercial}
      alt="Modern clean office workspace"
    />
  </section>
);

export const Residential = () => (
  <section className="py-24 md:py-28 bg-white" data-testid="residential-section">
    <Block
      id="residential"
      reverse
      testid="residential-block"
      overline="Residential Cleaning"
      title="A cleaner home without the stress."
      body="Enjoy a cleaner, healthier home without sacrificing your valuable time. Recurring service, one-time deep cleans, or move-in/move-out service — a customized plan tailored to your lifestyle."
      points={[
        "Recurring Housekeeping",
        "Deep Cleaning",
        "Move In / Out",
        "One-Time",
        "Kitchens & Baths",
      ]}
      image={IMAGES.residential}
      alt="Bright clean modern living room"
    />
  </section>
);
